import { useContext, useEffect, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// MUI
import { Grid } from "@mui/material";

// Third party
import moment from "moment";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { getMyOEMChevroletCsi, getMyOEMMitsubishiCsi, getMyOEMVolvoCsi } from "@DataPages/api/myOEM";

// Context
import { OEMContext } from "@DataPages/context/oem/OEMContext";

// Components
import MyOEMDataContent from "./MyOEMDataContent";
import MitsubishiDataContent from "./MitsubishiDataContent";
import VolvoDataContent from "./VolvoDataContent";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const getLastUpdatedDate = (date, dateFormat = DATE_FORMAT.usDateTime12hUnpadded) => {
	if (date) {
		return moment(date).format(dateFormat);
	}
	return null;
};

export default function OEMDataBody(props) {
	const { franchise } = useContext(OEMContext);
	const subscriptionId = useRef(null);
	const [ingesting, setIngesting] = useState(false);
	const location = useSelector((state) => state.location.location);
	const queryClient = useQueryClient();

	const user = useSelector((state) => state.user.user);

	// Permissions
	const { getSubscription } = useSubscriptions();
	const oemDataSubscription = getSubscription(["myOemData", props.selected?._id]);

	oemDataSubscription?.subscriptions?.onCreateOEMChevroletCsi?.setCallback((result) => {
		const performance = result.data?.onCreateOEMChevroletCsi;
		const commissionCycleId = oemDataSubscription.subscriptions.onCreateOEMChevroletCsi.getVariables().commissionCycleId;

		if (performance) {
			queryClient.setQueryData(["myOemData", location?.locationId, props.selected?._id, franchise], () => performance);
		}

		if (subscriptionId.current === commissionCycleId) {
			setIngesting(false);
		}
	});

	const { data: oemData, isLoading } = useQuery({
		queryKey: ["myOemData", location?.locationId, props.selected?._id, franchise],
		queryFn: async () => getMyOEMChevroletCsi(location.locationId, props.selected?._id, user?._id),
		enabled: !!location?.locationId && !!props.selected?._id && !!(franchise === "Chevrolet"),
		staleTime: Infinity,
	});

	oemDataSubscription?.subscriptions?.onCreateOEMMitsubishiCsi?.setCallback((result) => {
		const performance = result.data?.onCreateOEMMitsubishiCsi;
		const commissionCycleId = oemDataSubscription.subscriptions.onCreateOEMMitsubishiCsi.getVariables().commissionCycleId;

		if (performance) {
			queryClient.setQueryData(["myOemData", location?.locationId, "mitsubishi", props.selected?._id, franchise], () => performance);
		}

		if (subscriptionId.current === commissionCycleId) {
			setIngesting(false);
		}
	});

	const { data: mitsubishiOEMData, isLoading: mitsubishiLoading } = useQuery({
		queryKey: ["myOemData", location?.locationId, "mitsubishi", props.selected?._id, franchise],
		queryFn: () => getMyOEMMitsubishiCsi(location.locationId, props.selected?._id, user?._id),
		enabled: !!location?.locationId && !!props.selected?._id && !!(franchise === "Mitsubishi"),
		staleTime: Infinity,
	});

	oemDataSubscription?.subscriptions?.onCreateOEMVolvoCsi?.setCallback((result) => {
		const performance = result.data?.onCreateOEMVolvoCsi;
		const commissionCycleId = oemDataSubscription.subscriptions.onCreateOEMVolvoCsi.getVariables().commissionCycleId;

		if (performance) {
			queryClient.setQueryData(["myOemData", location?.locationId, "volvo", props.selected?._id, franchise], () => performance);
		}

		if (subscriptionId.current === commissionCycleId) {
			setIngesting(false);
		}
	});

	const { data: volvoOEMData, isLoading: volvoLoading } = useQuery({
		queryKey: ["myOemData", location?.locationId, "volvo", props.selected?._id, franchise],
		queryFn: () => getMyOEMVolvoCsi(location.locationId, props.selected?._id, user?._id),
		enabled: !!location?.locationId && !!props.selected?._id && !!(franchise === "Volvo" || franchise === "Volvo Cars"),
		staleTime: Infinity,
	});

	useEffect(() => {
		subscriptionId.current = props.selected?._id || null;

		// Lifting the state up to the Layer.
		switch (franchise) {
			case "Chevrolet":
				props?.updateData(getLastUpdatedDate(oemData?.lastModifiedOn));
				break;
			case "Mitsubishi":
				props?.updateData(getLastUpdatedDate(mitsubishiOEMData?.lastModifiedOn));
				break;
			case "Volvo":
			case "Volvo Cars":
				props?.updateData(getLastUpdatedDate(volvoOEMData?.lastModifiedOn));
				break;
			default:
				props?.updateData(null);
				break;
		}

		return () => {
			subscriptionId.current = null;
			setIngesting(false);
		};
	}, [franchise, location, mitsubishiOEMData, oemData, volvoOEMData, props]);

	return (
		<Grid container alignContent={"flex-start"} sx={{ height: "100%" }}>
			<Grid container item alignContent={"space-between"} sx={{ height: "100%" }}>
				<Grid item xs={12}>
					{franchise === "Chevrolet" && (
						<MyOEMDataContent
							referenceMetrics={oemData?.referenceMetrics || []}
							employeeMetrics={oemData?.employeeMetrics || []}
							isDataPresent={Boolean(oemData?.referenceMetrics?.length || oemData?.employeeMetrics?.length)}
							loading={isLoading || ingesting}
						/>
					)}
					{franchise === "Mitsubishi" && (
						<MitsubishiDataContent
							referenceMetrics={mitsubishiOEMData?.referenceMetrics || []}
							employeeMetrics={mitsubishiOEMData?.employeeMetrics || []}
							isDataPresent={Boolean(
								mitsubishiOEMData?.referenceMetrics?.length || mitsubishiOEMData?.employeeMetrics?.length
							)}
							loading={mitsubishiLoading || ingesting}
							selected={props.selected}
						/>
					)}
					{(franchise === "Volvo" || franchise === "Volvo Cars") && (
						<VolvoDataContent
							referenceMetrics={volvoOEMData?.referenceMetrics || []}
							employeeMetrics={volvoOEMData?.employeeMetrics || []}
							isDataPresent={Boolean(volvoOEMData?.referenceMetrics?.length || volvoOEMData?.employeeMetrics?.length)}
							loading={volvoLoading || ingesting}
							selected={props.selected}
						/>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
}
